const { getElement, getElements } = window.BAO.Utils

class BaoBundleComponents extends window.BAO.CustomElement() {
  #searchParamKey = 'component_option_values'
  #shopifySectionId = 'shopify-section-bao-app-bundle-component'
  #abortControllers = new Map()

  static get requiredElements() {
    return [
      'component',
      'legend',
      'option',
      'radio',
      'input',
      'thumbnail',
      'image-assignments',
      'notify-me-variant-id',
    ]
  }

  static get requiredExternalElements() {
    return [
      'atc-button',
    ]
  }

  connectedCallback() {
    super.connectedCallback()

    this.setSelectedOptionsFromUrl()
  }

  setupListeners() {
    super.setupListeners()

    this.handleRadioChange = this.handleRadioChange.bind(this)
    this.setSelectedOptionsFromUrl = this.setSelectedOptionsFromUrl.bind(this)

    this.addRadioListeners()
    this.listeners.add(window, 'popstate', this.setSelectedOptionsFromUrl)

    this.listeners.add(document, 'click', this.handleAddToCartClick)
  }

  addRadioListeners() {
    this.els?.radio?.elements?.forEach(radioEl => {
      this.listeners.add(radioEl, 'change', this.handleRadioChange)
    })
  }

  removeRadioListeners() {
    this.els?.radio?.elements?.forEach(radioEl => {
      this.listeners.remove(radioEl, 'change', this.handleRadioChange)
    })
  }

  /**
   * Handles the radio change event
   *
   * @param {Event} event
   */
  async handleRadioChange(event) {
    const componentEl = event.target.closest(this.els.component.selector)
    if (!componentEl) return

    if (event.target.dataset.optionType === 'style') {
      const coverageRadios = getElements(`${this.els.radio.selector}[data-option-type="coverage"]`, { context: componentEl })
      coverageRadios.forEach(radio => {
        radio.checked = false
      })
    }

    const checkedRadioEls = this.els?.radio?.elements?.filter(el => el.checked) ?? []
    const optionValues = checkedRadioEls.map(el => el.dataset.valueId)

    this.updateLegendTexts()

    await this.renderComponents([componentEl], optionValues, { fromUrl: false })

    if (event.isTrusted) getElement(`#${event.target.id}`, { context: document })?.focus()

    this.updateUrl(optionValues)
  }

  /**
   * Renders a list of components
   *
   * @param {HTMLElement[]} componentEls
   * @param {string[]} [optionValues=[]]
   */
  async renderComponents(componentEls, optionValues = [], options = { fromUrl: false }) {
    try {
      this.setIsLoading(true, { force: true })
      await Promise.all(componentEls.map(componentEl => this.renderComponent(componentEl, optionValues, options)))
    } catch (error) {
      if (error.name === 'AbortError') return

      console.error(error)
    } finally {
      this.setIsLoading(false)
      this.setThumbnailUrl()
      this.checkValidity()
    }
  }

  handleAddToCartClick(event) {
    if (!this.externalEls?.['atc-button']?.selector) return

    const buttonEl = event.target.closest(this.externalEls['atc-button'].selector)
    if (!buttonEl) return

    if (buttonEl.getAttribute('aria-disabled') !== 'true') {
      return true
    }

    event.preventDefault()
    event.stopPropagation()
  }

  /**
   * Renders a component
   *
   * @param {HTMLElement} componentEl
   * @param {string[]} [optionValues=[]]
   */
  async renderComponent(componentEl, optionValues = [], options = { fromUrl: false }) {
    const componentCheckedValueIds = getElements(this.els.radio.selector, { context: componentEl })
      .filter(el => el.checked)
      .map(el => el.dataset.valueId)
    const controller = new AbortController()
    const { componentUrl, componentId, sectionId } = componentEl.dataset

    this.#abortControllers.get(componentId)?.abort()
    this.#abortControllers.set(componentId, controller)

    const sectionUrl = `${componentUrl}?section_id=${sectionId}&option_values=${optionValues.join(',')}`

    try {
      const htmlString = await window.BAO.sectionRenderer.fetchSingle(sectionId, sectionUrl, { signal: controller.signal })
      const html = window.BAO.sectionRenderer.getOuterHtml(htmlString, { id: this.#shopifySectionId, selector: `[data-section-id="${sectionId}"][data-component-id="${componentId}"]` })

      componentEl.outerHTML = html || componentEl.outerHTML

      const newComponentEl = getElement(`[data-section-id="${sectionId}"][data-component-id="${componentId}"]`, { context: this })
      if (newComponentEl) {
        const optionEls = getElements(this.els.option.selector, { context: newComponentEl })
        optionEls.forEach(optionEl => {
          const radioEls = getElements(this.els.radio.selector, { context: optionEl })
          radioEls.forEach(radioEl => {
            const shouldBeChecked = options.fromUrl
              ? optionValues.includes(radioEl.dataset.valueId)
              : componentCheckedValueIds.includes(radioEl.dataset.valueId)

            radioEl.checked = shouldBeChecked
          })
        })
      }
    } catch (error) {
      if (error.name === 'AbortError') return

      console.error(error)
    } finally {
      if (this.#abortControllers.get(componentId) === controller) {
        this.#abortControllers.delete(componentId)
      }
    }
  }

  checkValidity() {
    const optionEls = this.els?.option?.elements ?? []
    const checkedRadioEls = this.els?.radio?.elements?.filter(el => el.checked) ?? []
    const isAnyCheckedUnavailable = checkedRadioEls.some(el => el.dataset.available === 'false')

    this.toggleAddToCartButton(optionEls.length !== checkedRadioEls.length || isAnyCheckedUnavailable, true)
    this.setNotifyMeVariantId(checkedRadioEls)
    this.setGalleryImage(checkedRadioEls)
  }

  setThumbnailUrl() {
    const thumbnailEl = this.els?.thumbnail?.element
    const variantIds = this.els?.component?.elements?.map(componentEl => componentEl.dataset?.variantId).filter(Boolean)
    const imageAssignments = this.getImageAssignments()
    const thumbnailUrl = imageAssignments.find(imageAssignment => variantIds.some(variantId => imageAssignment.variantIds.some(imageVariantId => String(imageVariantId) === variantId)))?.imageUrl ?? ''

    if (!thumbnailEl) return

    thumbnailEl.value = thumbnailUrl
  }

  /**
   * Sets the selected options from the URL
   */
  async setSelectedOptionsFromUrl() {
    if (this.isInQuickAddModal) {
      this.checkValidity()

      return
    }

    const searchParams = new URLSearchParams(window.location.search)
    const optionValues = searchParams.get(this.#searchParamKey)?.split(',')
    if (!optionValues?.length) {
      this.checkValidity()

      return
    }

    await this.renderComponents(this.els?.component?.elements ?? [], optionValues, { fromUrl: true })
  }

  /**
   * Sets the loading state
   *
   * @param {boolean} isLoading
   * @param {{ force: boolean }} options
   */
  setIsLoading(isLoading, { force = false } = {}) {
    if (!isLoading && this.#abortControllers.size > 0 && !force) return

    this.setAttribute('aria-busy', isLoading)
    this.toggleAddToCartButton(isLoading)

    if (isLoading) {
      this.removeRadioListeners()
    } else {
      this.setInputValue()
      this.addRadioListeners()
      this.ensureEachOptionHasSelectedValue()
      this.updateLegendTexts()
    }
  }

  /**
   * Ensures the selected options are set
   */
  ensureEachOptionHasSelectedValue() {
    const optionEls = this.els?.option?.elements ?? []

    optionEls.forEach(optionEl => {
      if (optionEl.dataset.optional === 'true') {
        const hasSelected = getElement(`${this.els.radio.selector}:checked`, { context: optionEl })
        if (!hasSelected) return
      }

      const selectedRadioEl = getElement(`${this.els.radio.selector}:checked`, { context: optionEl })
      if (selectedRadioEl) return

      const firstRadioEl = getElement(this.els.radio.selector, { context: optionEl })

      if (firstRadioEl) {
        firstRadioEl.checked = true
        firstRadioEl.dispatchEvent(new Event('change'))
      }
    })
  }

  /**
   * Updates the legend text for each option
   */
  updateLegendTexts() {
    const optionEls = this.els?.option?.elements ?? []

    optionEls.forEach(optionEl => {
      const legendEl = getElement(this.els.legend.selector, { context: optionEl })
      if (!legendEl) return

      const checkedRadioEl = getElement(`${this.els.radio.selector}:checked`, { context: optionEl })

      if (checkedRadioEl) {
        legendEl.dataset.selectedText = `${legendEl.dataset.defaultText}: ${checkedRadioEl.value}`
        legendEl.textContent = legendEl.dataset.selectedText
      } else {
        legendEl.textContent = legendEl.dataset.defaultText
      }
    })
  }

  /**
   * Sets the input element value
   */
  setInputValue() {
    const inputEl = this.els?.input?.element
    if (!inputEl) return

    inputEl.value = this.els?.component?.elements?.map(componentEl => `${componentEl.dataset.componentId}:${componentEl.dataset.variantId}`).join(',') ?? inputEl.value
  }

  /**
   * Updates the URL with the selected option values
   *
   * @param {string[]} optionValues
   */
  updateUrl(optionValues) {
    if (this.isInQuickAddModal) return

    const searchParams = new URLSearchParams(window.location.search)
    searchParams.set(this.#searchParamKey, optionValues.join(','))

    window.history.pushState({}, '', `${document.location.pathname}?${searchParams.toString()}`)
  }

  setNotifyMeVariantId(checkedRadioEls) {
    const notifyMeVariantIdEl = this.els?.['notify-me-variant-id']?.element
    if (!notifyMeVariantIdEl) return

    const variantId = checkedRadioEls.find(el => el.dataset.available === 'false')?.dataset?.variantId
    notifyMeVariantIdEl.value = variantId || notifyMeVariantIdEl.value
  }

  /**
   * Set gallery image
   *
   * @param {HTMLInputElement[]} checkedRadioEls
   */
  setGalleryImage(checkedRadioEls) {
    const value = checkedRadioEls.filter(el => {
      const optionType = el.dataset.optionType?.toLowerCase() ?? ''

      return optionType === 'style' || optionType === 'coverage'
    }).map(el => el.value ?? null).filter(Boolean).join('_')

    const sanitizedValue = value.replace(/-/g, '_').replace(/ /g, '_').toLowerCase()
    window.BAO.dispatchEvent(window.BAO.EVENTS.BUNDLE_COMPONENT.OPTION_SELECTED, { value: sanitizedValue })
  }

  /**
   * Toggles the add to cart button
   *
   * @param {boolean} [disabled=false]
   * @param {boolean} [updateText=false]
   */
  toggleAddToCartButton(disabled = false, updateText = false) {
    const parentIsUnavailable = this.dataset.available === 'false'
    const shouldBeDisabled = disabled || parentIsUnavailable

    this.setAttribute('data-is-sold-out', shouldBeDisabled)

    const { buttonEls, atcTextEls, atcTextWithUpsellEls, oosTextEls } = this.getAddToCartButtonEls()

    if (!buttonEls?.length || !atcTextEls?.length || !atcTextWithUpsellEls?.length || !oosTextEls?.length) return

    const deselectedOptionEl = this.getFirstDeselectedOptionEl()

    buttonEls.forEach(buttonEl => {
      buttonEl.setAttribute('data-notify-me', shouldBeDisabled && !deselectedOptionEl)
      buttonEl.setAttribute('aria-disabled', shouldBeDisabled)
    })

    if (!updateText) return

    this.toggleAddToCartButtonText()
  }

  toggleAddToCartButtonText() {
    const hasUpsell = this.dataset.hasUpsell === 'true'
    const isSoldOut = this.dataset.isSoldOut === 'true'

    const { atcTextEls, atcTextWithUpsellEls, oosTextEls, customButtonTextEls } = this.getAddToCartButtonEls()
    if (!atcTextEls.length || !atcTextWithUpsellEls.length || !oosTextEls.length || !customButtonTextEls.length) return

    const deselectedOptionEl = this.getFirstDeselectedOptionEl()

    if (deselectedOptionEl?.dataset?.selectOptionText?.length) {
      customButtonTextEls.forEach(el => {
        el.textContent = deselectedOptionEl.dataset.selectOptionText
      })

      this.toggleElementVisiblity(atcTextEls, true)
      this.toggleElementVisiblity(atcTextWithUpsellEls, true)
      this.toggleElementVisiblity(oosTextEls, true)
      this.toggleElementVisiblity(customButtonTextEls, false)

      return
    }

    this.toggleElementVisiblity(atcTextEls, hasUpsell || isSoldOut)
    this.toggleElementVisiblity(atcTextWithUpsellEls, !hasUpsell || isSoldOut)
    this.toggleElementVisiblity(oosTextEls, !isSoldOut)
    this.toggleElementVisiblity(customButtonTextEls, true)
  }

  /**
   * Toggles the visibility of elements
   *
   * @param {HTMLElement[]} els
   * @param {boolean} isHidden
   */
  toggleElementVisiblity(els, isHidden) {
    els.forEach(el => el.setAttribute('aria-hidden', isHidden))
  }

  /**
   * Gets the first deselected option element
   *
   * @returns {HTMLElement | null} The first deselected option element
   */
  getFirstDeselectedOptionEl() {
    if (!this.els?.option?.elements?.length) return null

    return this.els.option.elements.find(optionEl => !getElement(`${this.els.radio.selector}:checked`, { context: optionEl }))
  }

  /**
   * Gets the image assignments
   *
   * @returns {{ id: string, imageUrl: string, variantIds: number[] }[]} The image assignments
   */
  getImageAssignments() {
    const imageAssignmentEl = this.els?.['image-assignments']?.element
    return imageAssignmentEl?.textContent ? JSON.parse(imageAssignmentEl.textContent) : []
  }

  getAddToCartButtonEls() {
    return {
      buttonEls: this.externalEls?.['atc-button']?.elements,
      atcTextEls: getElements('[data-atc-button-text]', { context: document }),
      atcTextWithUpsellEls: getElements('[data-atc-button-text-with-upsell]', { context: document }),
      oosTextEls: getElements('[data-oos-button-text]', { context: document }),
      customButtonTextEls: getElements('[data-custom-button-text]', { context: document }),
    }
  }

  /**
   * Checks if the component is in a quick add modal
   * @returns {boolean}
   */
  get isInQuickAddModal() {
    return !!this.closest('quick-add-modal')
  }
}

if (!window.customElements.get('bao-app-bundle-components')) {
  window.customElements.define('bao-app-bundle-components', BaoBundleComponents)
}
